import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {createPortal} from 'react-dom';
import Modal from 'react-modal';
import _ from 'lodash';

import {ClearIcon, ChevronIcon} from './svg';
import {useWindowSize} from '../hooks';
import {cx} from '../util';

const modalScreenMinSize = 768;

export const defaultNonNativeOptionsStyles = {
  width: 300,
  maxWidth: 300,
  top: 48,
  maxHeight: 500,
};

const NativeOptions = ({options, active, isGrouped}) => (isGrouped ? (
  <>
    {options.map((optionGroup) => (
      <optgroup label={optionGroup.label}>
        {optionGroup.options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            selected={active && active.value === option.value ? 'selected' : null}
          >
            {option.label}
          </option>
        ))}
      </optgroup>
    ))}
  </>
) : (
  <>
    {options.map((option) => (
      <option
        key={option.value}
        value={option.value}
        selected={active && active.value === option.value ? 'selected' : null}
      >
        {option.label}
      </option>
    ))}
  </>
));

const NonNativeOptions = ({
  options, active, filter, onClick, isGrouped,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    const cleanFilter = (filter || '').trim().toLowerCase();
    const matchesFilter = ({label}) => label.toLowerCase().includes(cleanFilter);

    let filtered;
    if (isGrouped) {
      const optionGroups = options.map((optionGroup) => ({
        label: optionGroup.label,
        options: optionGroup.options.filter(matchesFilter),
      }));
      filtered = optionGroups.filter((optionGroup) => optionGroup.options.length);
    } else {
      filtered = options.filter(matchesFilter);
    }
    setFilteredOptions(filtered);
  }, [options, filter]);

  return (
    <>
      {isGrouped ? (filteredOptions.map((optionGroup) => (
        <>
          <div className="pt-4 pb-2 px-5 text-xs text-gray-400 font-semibold uppercase">
            {optionGroup.label}
          </div>
          {optionGroup.options.map((option) => (
            <DropdownItem
              option={option}
              active={active}
              onClick={() => onClick(option.value)}
              key={option.value}
            />
          ))}
        </>
      ))) : (
        filteredOptions.map((option) => (
          <DropdownItem
            option={option}
            active={active}
            onClick={() => onClick(option.value)}
            key={option.value}
          />
        )))}
      {filter && filteredOptions.length === 0 && (
        <span className="px-4 pt-2 pb-4 text-gray-600">
          {`No results for "${filter}"`}
        </span>
      )}
    </>
  );
};

const DropdownInput = ({
  id,
  value,
  placeholder,
  options = [],
  native,
  onChange,
  disabled,
  showFilter,
  showReset = true,
  highlightActive = true,
  className,
  useModalForSmallScreens = true,
  truncateLength = null,
  optionsStyles = defaultNonNativeOptionsStyles,
  align,
  ariaLabel,
  borderless = false,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState();
  const [portalPos, setPortalPos] = useState(null);
  const containerRef = useRef(null);
  const {width} = useWindowSize();

  const useModal = useModalForSmallScreens && width <= modalScreenMinSize;

  const updatePortalPos = useCallback(() => {
    // Anchor below the trigger by default; flip above only when there isn't
    // enough usable room below, to avoid unnecessarily covering the header.
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 10;
    const minUsable = 200;
    const desiredMaxHeight = optionsStyles.maxHeight || 500;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const flipAbove = spaceBelow < minUsable && spaceAbove > spaceBelow;
    setPortalPos({
      top: flipAbove ? undefined : rect.bottom + gap,
      bottom: flipAbove ? window.innerHeight - rect.top + gap : undefined,
      left: align === 'right' ? undefined : rect.left,
      right: align === 'right' ? window.innerWidth - rect.right : undefined,
      maxHeight: Math.min(desiredMaxHeight, flipAbove ? spaceAbove : spaceBelow),
    });
  }, [align, optionsStyles.maxHeight]);

  useEffect(() => {
    if (open && !useModal) {
      updatePortalPos();
      window.addEventListener('scroll', updatePortalPos, true);
      window.addEventListener('resize', updatePortalPos);
      return () => {
        window.removeEventListener('scroll', updatePortalPos, true);
        window.removeEventListener('resize', updatePortalPos);
      };
    }
    return undefined;
  }, [open, useModal, updatePortalPos]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const numOptionsSelected = Array.isArray(value) ? value.length : 0;
  const isGrouped = _.every(options, 'options');
  let active;
  if (isGrouped) {
    const allOptions = _.flatMap(options, 'options');
    active = _.isArray(value)
      ? allOptions.filter((o) => value.includes(o.value))
      : allOptions.find((o) => value === o.value);
  } else {
    active = _.isArray(value)
      ? options.filter((o) => value.includes(o.value))
      : options.find((o) => value === o.value);
  }

  const handleClick = (val) => {
    if (!disabled) {
      onChange(val);
      setOpen(false);
    }
  };

  const handleReset = (e) => {
    if (!disabled) {
      e.stopPropagation();
      onChange(null);
      setOpen(false);
    }
  };

  const handleFilter = (e) => {
    if (!disabled) {
      setFilter(e.target.value);
    }
  };

  const getDisplayValue = () => {
    if (active && numOptionsSelected > 1) {
      return `${numOptionsSelected} Selected`;
    }
    if (active) {
      return _.truncate(active.label, {length: truncateLength || active.label.length});
    }
    return placeholder;
  };

  const displayValue = getDisplayValue();
  const dropdownId = `${id || 'dropdown-input'}-listbox`;

  return (
    <div ref={containerRef} className={cx('relative', className)}>
      <button
        id={id}
        className={cx(`
          inline-flex
          items-center
          justify-between
          w-full
          rounded-lg
          pl-4
          pr-2
          py-2
          bg-white
          text-sm
          font-medium
          ${borderless ? '' : 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500'}
          whitespace-nowrap
          transition
          duration-50
        `, {
          'border border-indigo text-indigo shadow-sm': active && !disabled && highlightActive && !borderless,
          'border border-gray-300 text-gray-700 hover:border-gray-400 focus:border-gray-400 shadow-sm': (!active || !highlightActive) && !disabled && !borderless,
          'text-indigo': active && !disabled && highlightActive && borderless,
          'text-gray-700': (!active || !highlightActive) && !disabled && borderless,
          'border border-gray-300 text-gray-600 cursor-default': disabled && !borderless,
          'text-gray-600 cursor-default': disabled && borderless,
          'cursor-pointer': !disabled,
        })}
        onClick={() => !disabled && setOpen(!open)}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={dropdownId}
        aria-haspopup="listbox"
        aria-label={ariaLabel || placeholder || 'Select option'}
      >
        {native && (
          <select
            className={cx('h-full w-full absolute', {'cursor-pointer': !disabled, 'cursor-default': disabled})}
            onChange={(e) => handleClick(e.target.value)}
            style={{opacity: 0}}
            disabled={disabled}
            aria-label="Select"
          >
            <NativeOptions options={options} active={active} />
          </select>
        )}
        <span className="mr-1">{displayValue}</span>
        <ChevronIcon className="ml-auto" />
      </button>
      {active && showReset && (
        <button
          className={cx('absolute top-1/2 -translate-y-1/2 right-2.5 focus:outline-none z-10', {'text-gray-400': disabled, 'text-indigo-700': !disabled})}
          onClick={handleReset}
          type="button"
          aria-label="Clear"
        >
          <ClearIcon />
        </button>
      )}
      {!native && !useModal && open && portalPos && createPortal(
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
            aria-hidden
          />
          <ul
            id={dropdownId}
            className="fixed flex flex-col bg-white border border-gray-200 shadow-lg rounded-lg z-20 overflow-hidden"
            style={{
              width: optionsStyles.width,
              maxWidth: optionsStyles.maxWidth,
              maxHeight: portalPos.maxHeight ?? optionsStyles.maxHeight,
              top: portalPos.top,
              bottom: portalPos.bottom,
              left: portalPos.left,
              right: portalPos.right,
              minWidth: containerRef.current?.offsetWidth,
            }}
            role="listbox"
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          >
            <div className="flex flex-col overflow-auto h-full bg-white scrollbar-hidden">
              {showFilter && (
                <input
                  type="text"
                  placeholder="Filter..."
                  defaultValue={filter}
                  className="flex mt-3 mb-1 mx-3 px-2 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-400"
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  onChange={handleFilter}
                />
              )}
              <NonNativeOptions
                options={options}
                active={active}
                filter={filter}
                onClick={handleClick}
                isGrouped={isGrouped}
              />
            </div>
          </ul>
        </>,
        document.body,
      )}
      {useModal && (
        <Modal
          className={cx('z-20 fixed bottom-0 left-0 right-0 pt-3 w-full h-96 max-h-screen overflow-scroll bg-white border border-gray-200 shadow-lg rounded-lg',
            {invisible: !open})}
          overlayClassName={open ? 'z-10 bg-black/10 fixed inset-0' : 'invisible'}
          shouldCloseOnOverlayClick
          onRequestClose={() => setOpen(false)}
          isOpen
        >
          <ul className="flex flex-col">
            {showFilter && (
              <input
                type="text"
                placeholder="Filter..."
                defaultValue={filter}
                className="flex mb-1 mx-3 px-2 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-400"
                onChange={handleFilter}
              />
            )}
            <NonNativeOptions
              options={options}
              active={active}
              filter={filter}
              onClick={handleClick}
              isGrouped={isGrouped}
            />
          </ul>
        </Modal>
      )}
    </div>
  );
};

const DropdownItem = ({option, active, onClick}) => {
  const isActive = _.isArray(active)
    ? _.some(active, (activeItem) => activeItem.value === option.value)
    : active && option.value === active.value;

  const aClickHandler = (e) => {
    e.preventDefault();
    onClick();
  };

  return (
    <li>
      {option.url ? (
        <a
          className={cx('block w-full py-3 px-5 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 text-sm text-left whitespace-pre-wrap', {
            'text-indigo': isActive,
          })}
          onClick={aClickHandler}
          href={option.url}
          role="option"
          aria-selected={isActive}
        >
          {option.label}
        </a>
      ) : (
        <button
          className={cx('w-full py-3 px-5 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 text-sm text-left whitespace-pre-wrap', {
            'text-indigo': isActive,
          })}
          onClick={onClick}
          type="button"
          role="option"
          aria-selected={isActive}
        >
          {option.label}
        </button>
      )}
    </li>
  );
};

export default DropdownInput;
